/**
 * ActionChecklist — the "what do we DO about it" half of a risk panel.
 *
 * FloodDash and AirDash both return a `checklist` alongside their score: a
 * short ladder of concrete municipal actions written for the person running
 * a town ("assign a duty officer to check this board every 3h", "alert
 * village heads in low-lying zones"). The dashboard fetched that array and
 * threw it away, which left every risk panel stopping at a number.
 *
 * Ticks persist in localStorage under a key of (panel, band, calendar day):
 *
 *   - **day** in the key means yesterday's ticks never make today look
 *     handled. A checklist that stays ticked forever is worse than none.
 *   - **band** in the key means an escalation (watch <Arrow direction="right" /> high) presents a
 *     fresh, unticked list, because the actions the band demands changed.
 *
 * Progress is deliberately not reported anywhere but this browser: it is a
 * duty-officer's scratch pad, not an audit trail, and presenting it as the
 * latter would overclaim.
 */

import { useCallback, useEffect, useState } from "react";

export interface ChecklistItem {
  th: string;
  en: string;
}

interface Props {
  /** Stable id for the owning panel, e.g. "flood" | "air". */
  panelId: string;
  /** Current severity band — a change resets the list. */
  band: string;
  items: ChecklistItem[];
  /** Heading shown above the list. */
  title?: string;
}

function storageKey(panelId: string, band: string): string {
  const day = new Date().toISOString().slice(0, 10);
  return `sikhio:checklist:${panelId}:${band}:${day}`;
}

function loadTicks(key: string): Set<number> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((n): n is number => typeof n === "number")) : new Set();
  } catch (err) {
    // axiom-audit-ignore-next-line back -- corrupted localStorage cache; the read falls back to an empty Set which the UI handles honestly. Logging would be noise here.
    if (typeof console !== "undefined") console.warn("[ActionChecklist] cache read failed", err);
    return new Set();
    return new Set();
  }
}

export function ActionChecklist({ panelId, band, items, title = "สิ่งที่ต้องทำ · ACTION" }: Props) {
  const key = storageKey(panelId, band);
  const [ticked, setTicked] = useState<Set<number>>(() => loadTicks(key));

  // Re-read when the key changes (band escalated, or the day rolled over
  // while the board sat open on a wall).
  useEffect(() => { setTicked(loadTicks(key)); }, [key]);

  const toggle = useCallback((i: number) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      try {
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch (err) {
        // axiom-audit-ignore-next-line back -- localStorage private mode / quota; checklist is operator-only, no data-loss risk.
        if (typeof console !== "undefined") console.warn("[ActionChecklist] persist failed", err);
      }
      return next;
    });
  }, [key]);

  if (items.length === 0) return null;
  const done = items.filter((_, i) => ticked.has(i)).length;

  return (
    <div className="action-checklist">
      <div className="action-checklist-head">
        <span className="eyebrow mono">{title}</span>
        <span
          className="mono caption"
          style={{ color: done === items.length ? "var(--good)" : "var(--text-3)" }}
        >
          {done}/{items.length}
        </span>
      </div>
      <ul className="action-checklist-items" role="list">
        {items.map((item, i) => {
          const on = ticked.has(i);
          return (
            <li key={item.th}>
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                onClick={() => toggle(i)}
                className={`action-checklist-item ${on ? "is-done" : ""}`}
              >
                <span className="action-checklist-box mono" aria-hidden="true">{on ? "✓" : ""}</span>
                <span className="action-checklist-text">{item.th}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
